import Icon from "./icons";
import X from "lucide-react/icons/x";
import Menu from "lucide-react/icons/menu";
import Moon from "lucide-react/icons/moon";
import Sun from "lucide-react/icons/sun";
import ChevronDown from "lucide-react/icons/chevron-down";
import Github from "lucide-react/icons/github";
import ShoppingCart from "lucide-react/icons/shopping-cart";
import { appContext } from "../hooks/provider";
import { Link, useLocation } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { labFiles } from "../data/labs";
import { LanguageSwitcher } from "./common";
import { CHECKOUT_URL } from "../data/siteLinks";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const Header = ({ meta }: any) => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const link = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLabsDropdownOpen, setIsLabsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLabsDropdownOpen(false);
      }
    }

    if (isLabsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLabsDropdownOpen]);

  const links = [
    {
      name: (
        <div>
          <Icon icon="app" size={4} />
        </div>
      ),
      href: "/",
    },
    {
      name: (
        <div
          className="flex items-center cursor-pointer relative"
          onClick={() => setIsLabsDropdownOpen(!isLabsDropdownOpen)}
        >
          <Icon icon="labs" size={4} className="text-accent mr-1" /> {t("nav.labs")}
          <ChevronDown
            className={`h-3 w-3 ml-1 transition-transform duration-200 ${
              isLabsDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      ),
      href: "/labs",
      isDropdown: true,
    },
    {
      name: t("nav.news"),
      href: "/news",
    },
    {
      name: (
        <div className="flex items-center">
          <ShoppingCart className="h-4 w-4 mr-1" /> {t("nav.buy")}
        </div>
      ),
      href: CHECKOUT_URL,
      isExternal: true,
    },
  ];

  const DarkModeToggle = () => {
    return (
      <appContext.Consumer>
        {(context: any) => {
          return (
            <button
              onClick={() => {
                if (context.darkMode === "dark") {
                  context.setDarkMode("light");
                } else {
                  context.setDarkMode("dark");
                }
              }}
              type="button"
              className="flex-shrink-0 bg-primary p-1 text-secondary rounded-full hover:text-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
            >
              <span className="sr-only">{t("header.toggleDarkMode")}</span>
              {context.darkMode === "dark" && (
                <Moon className="h-6 w-6" aria-hidden="true" />
              )}
              {context.darkMode === "light" && (
                <Sun className="h-6 w-6" aria-hidden="true" />
              )}
            </button>
          );
        }}
      </appContext.Consumer>
    );
  };
  return (
    <nav className="bg-primary mt-4 text-primary mb-8 border-b border-secondary">
      <div className="px-0 sm:px-0 lg:px-0">
        <div className="flex justify-between h-16">
          <div className="flex lg:px-0">
            <div className="flex flex-shrink-0 pt-2">
              <Link className="block hover:cursor-pointer" to="/">
                <span className="text-accent inline-block pt-2 absolute">
                  <Icon icon="app" size={8} />
                </span>
                <div className="pt-1 text-lg ml-14 inline-block">
                  <div className="flex flex-col">
                    <div className="text-base">{t("header.designing")}</div>
                    <div className="text-xs">{t("header.multiAgentSystems")}</div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="hidden md:ml-6 md:flex md:space-x-6">
              {links.map((data, index) => {
                const isActive =
                  data.href === link ||
                  (link?.startsWith(data.href) && data.href !== "/");
                const activeClass = isActive ? "bg-accent" : "bg-secondary";

                if (data.isDropdown) {
                  return (
                    <div
                      key={index + "linkrow"}
                      className="text-primary items-center hover:text-accent px-1 pt-1 block text-sm font-medium relative"
                      ref={dropdownRef}
                    >
                      <div className="h-full flex flex-col">
                        <div className="flex-1 flex-col flex">
                          <div className="flex-1"></div>
                          <div className="pb-2 px-3">{data.name}</div>
                        </div>
                        <div
                          className={`${activeClass} w-full h-1 rounded-t-lg`}
                        ></div>
                      </div>
                      
                      {/* Labs dropdown */}
                      {isLabsDropdownOpen && (
                        <div className="absolute top-full left-0 mt-1 w-64 rounded-md shadow-lg py-2 bg-primary border border-secondary z-50">
                          {labFiles.map((file, idx) => (
                            <Link
                              key={idx}
                              to={file.url}
                              className="block px-4 py-2.5 text-sm font-medium text-primary hover:bg-secondary hover:text-accent transition-colors duration-150"
                              onClick={() => setIsLabsDropdownOpen(false)}
                            >
                              {t(file.titleKey, file.title)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                if (data.isExternal) {
                  return (
                    <div
                      key={index + "linkrow"}
                      className="text-primary items-center hover:text-accent px-1 pt-1 block text-sm font-medium"
                    >
                      <a
                        className="hover:text-accent h-full flex flex-col"
                        href={data.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex-1 flex-col flex">
                          <div className="flex-1"></div>
                          <div className="pb-2 px-3">{data.name}</div>
                        </div>
                        <div
                          className={`bg-secondary w-full h-1 rounded-t-lg`}
                        ></div>
                      </a>
                    </div>
                  );
                }

                return (
                  <div
                    key={index + "linkrow"}
                    className="text-primary items-center hover:text-accent px-1 pt-1 block text-sm font-medium"
                  >
                    <Link
                      className="hover:text-accent h-full flex flex-col"
                      to={data.href}
                    >
                      <div className="flex-1 flex-col flex">
                        <div className="flex-1"></div>
                        <div className="pb-2 px-3">{data.name}</div>
                      </div>
                      <div
                        className={`${activeClass} w-full h-1 rounded-t-lg`}
                      ></div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-secondary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent"
            >
              <span className="sr-only">{t("header.openMainMenu")}</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="hidden lg:ml-4 md:flex md:items-center md:space-x-2">
            <LanguageSwitcher />
            <a
              href="https://github.com/victordibia/designing-multiagent-systems"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 bg-primary p-1 text-secondary rounded-full hover:text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              aria-label="View GitHub repository"
            >
              <Github className="h-6 w-6" aria-hidden="true" />
            </a>
            <DarkModeToggle />
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {links.map((data, index) => {
              if (data.isDropdown) {
                return (
                  <div key={index + "dropdown-mobile"}>
                    <div className="bg-secondary border-accent text-accent block pl-3 pr-4 py-2 border-l-4 text-base font-medium rounded-l mb-1">
                      <Icon
                        icon="labs"
                        size={4}
                        className="text-accent mr-1 inline-block"
                      />{" "}
                      {t("nav.labs")}
                    </div>
                    {labFiles.map((file, idx) => (
                      <Link
                        key={idx + "lab-mobile"}
                        to={file.url}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="bg-primary border-secondary text-primary block pl-6 pr-4 py-2 border-l-2 text-sm font-medium rounded-l ml-2"
                      >
                        {t(file.titleKey, file.title)}
                      </Link>
                    ))}
                  </div>
                );
              }

              if (data.isExternal) {
                return (
                  <a
                    key={index + "linkrow-mobile"}
                    href={data.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="bg-secondary border-accent text-accent block pl-3 pr-4 py-2 border-l-4 text-base font-medium rounded-l"
                  >
                    {data.name}
                  </a>
                );
              }

              return (
                <Link
                  key={index + "linkrow-mobile"}
                  to={data.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="bg-secondary border-accent text-accent block pl-3 pr-4 py-2 border-l-4 text-base font-medium rounded-l"
                >
                  {data.name}
                </Link>
              );
            })}
          </div>
          <div className="mt-3 space-y-1 pb-2 flex items-center justify-between px-3">
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <span className="mr-2">{t("nav.darkMode")}</span>
              <DarkModeToggle />
            </div>
            <a
              href="https://github.com/victordibia/designing-multiagent-systems"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-secondary hover:text-primary"
              aria-label="View GitHub repository"
            >
              <Github className="h-6 w-6" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;